<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  teachingExperiences: {
    type: Array,
    default: () => []
  },
  subjects: {
    type: Array,
    default: () => []
  },
  qualificationOptions: {
    type: Object,
    default: () => ({})
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

// A-Level subjects from database (using subjects prop)
const aLevelSubjects = computed(() => {
  if (!props.subjects || props.subjects.length === 0) {
    return ['Other']
  }
  return [...props.subjects.map(s => s.name), 'Other']
})

// Get options from props with fallbacks
const aLevelGrades = computed(() => props.qualificationOptions?.aLevelGrades || ['A*', 'A', 'B', 'C', 'D', 'E'])

const degreeStatuses = computed(() => props.qualificationOptions?.degreeStatuses || [
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' }
])

const degreeTypes = computed(() => props.qualificationOptions?.degreeTypes || ['BSc', 'BA', 'MSc', 'MA', 'PhD', 'PGCE', 'Other'])

const degreeClassifications = computed(() => props.qualificationOptions?.degreeClassifications || [
  'First Class Honours',
  'Upper Second Class (2:1)',
  'Lower Second Class (2:2)',
  'Third Class Honours',
  'Pass',
  'In Progress',
  'Not Applicable'
])

// Teaching experience ranges from props (using subheading for years range)
const experienceRanges = computed(() => {
  if (!props.teachingExperiences || props.teachingExperiences.length === 0) {
    return []
  }
  return props.teachingExperiences.map(exp => ({
    value: exp.subheading?.replace(' years', '') || exp.id.toString(),
    label: exp.subheading?.replace(' years', '') || exp.name,
    description: exp.name,
    teaching_experience_id: exp.id
  }))
})

// Generate years (back to 2000, forward 5 years from current year)
const currentYear = new Date().getFullYear()
const maxYear = currentYear + 5
const startYears = Array.from({ length: maxYear - 1999 }, (_, i) => maxYear - i)
const endYears = startYears

const updateField = (field, value) => {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
  if (localErrors.value[field]) {
    delete localErrors.value[field]
  }
}

// Degree management
const addDegree = () => {
  const degrees = [...(props.modelValue.degrees || [])]
  degrees.push({
    status: '',
    degree_type: '',
    title: '',
    institution: '',
    start_year: '',
    end_year: '',
    classification: ''
  })
  updateField('degrees', degrees)

  if (localErrors.value.degrees) {
    delete localErrors.value.degrees
  }
}

const removeDegree = (index) => {
  const degrees = [...(props.modelValue.degrees || [])]
  degrees.splice(index, 1)
  updateField('degrees', degrees)
}

const updateDegree = (index, field, value) => {
  const degrees = [...(props.modelValue.degrees || [])]
  degrees[index] = { ...degrees[index], [field]: value }
  updateField('degrees', degrees)

  if (localErrors.value[`degrees.${index}.${field}`]) {
    delete localErrors.value[`degrees.${index}.${field}`]
  }
}

// A-Level management
const addALevel = () => {
  const aLevels = [...(props.modelValue.a_levels || [])]
  aLevels.push({ subject: '', grade: '' })
  updateField('a_levels', aLevels)

  if (localErrors.value.a_levels) {
    delete localErrors.value.a_levels
  }
}

const removeALevel = (index) => {
  const aLevels = [...(props.modelValue.a_levels || [])]
  aLevels.splice(index, 1)
  updateField('a_levels', aLevels)
}

const updateALevel = (index, field, value) => {
  const aLevels = [...(props.modelValue.a_levels || [])]
  aLevels[index] = { ...aLevels[index], [field]: value }
  updateField('a_levels', aLevels)

  if (localErrors.value[`a_levels.${index}.${field}`]) {
    delete localErrors.value[`a_levels.${index}.${field}`]
  }
}

const selectExperience = (experience) => {
  emit('update:modelValue', {
    ...props.modelValue,
    years_of_experience: experience.value,
    teaching_experience_id: experience.teaching_experience_id
  })

  if (localErrors.value.years_of_experience) {
    delete localErrors.value.years_of_experience
  }
  if (localErrors.value.teaching_experience_id) {
    delete localErrors.value.teaching_experience_id
  }
}

// Initialize with one empty degree and one empty A-level if none exist
const initializeDefaults = () => {
  let updated = false
  const updates = { ...props.modelValue }

  if (!updates.degrees || updates.degrees.length === 0) {
    updates.degrees = [{
      status: '',
      degree_type: '',
      title: '',
      institution: '',
      start_year: '',
      end_year: '',
      classification: ''
    }]
    updated = true
  }

  if (!updates.a_levels || updates.a_levels.length === 0) {
    updates.a_levels = [{ subject: '', grade: '' }]
    updated = true
  }

  if (updated) {
    emit('update:modelValue', updates)
  }
}

// Check if can remove (must have more than 1 item)
const canRemoveDegree = computed(() => (props.modelValue.degrees?.length || 0) > 1)
const canRemoveALevel = computed(() => (props.modelValue.a_levels?.length || 0) > 1)

//Tailwind breakpoint for labels
const breakpoints = useBreakpoints(breakpointsTailwind)
const selectYearLabel = computed(() => breakpoints.greaterOrEqual('md').value ? 'Select year' : 'Year')


// Initialize on mount and when modelValue changes from empty
onMounted(() => {
  initializeDefaults()
})

watch(() => props.modelValue, (newVal) => {
  if ((!newVal.degrees || newVal.degrees.length === 0) || (!newVal.a_levels || newVal.a_levels.length === 0)) {
    initializeDefaults()
  }
}, { deep: true })

const validate = () => {
  const errors = {}

  // Validate degrees - at least one is required
  if (!props.modelValue.degrees || props.modelValue.degrees.length === 0) {
    errors.degrees = 'Please add at least one degree/qualification'
  } else {
    props.modelValue.degrees.forEach((degree, index) => {
      if (!degree.status) {
        errors[`degrees.${index}.status`] = 'Status is required'
      }
      if (!degree.degree_type) {
        errors[`degrees.${index}.degree_type`] = 'Degree type is required'
      }
      if (!degree.title?.trim()) {
        errors[`degrees.${index}.title`] = 'Qualification title is required'
      }
      if (!degree.institution?.trim()) {
        errors[`degrees.${index}.institution`] = 'Institution is required'
      }
      if (!degree.start_year) {
        errors[`degrees.${index}.start_year`] = 'Start year is required'
      }
    })
  }

  // Validate teaching experience
  if (!props.modelValue.years_of_experience) {
    errors.years_of_experience = 'Please select your years of teaching experience'
  }

  // Validate A-Levels - at least one is required
  if (!props.modelValue.a_levels || props.modelValue.a_levels.length === 0) {
    errors.a_levels = 'Please add at least one A-Level qualification'
  } else {
    props.modelValue.a_levels.forEach((aLevel, index) => {
      if (!aLevel.subject && aLevel.grade) {
        errors[`a_levels.${index}.subject`] = 'Subject is required'
      }
      if (aLevel.subject && !aLevel.grade) {
        errors[`a_levels.${index}.grade`] = 'Grade is required'
      }
      if (!aLevel.subject && !aLevel.grade) {
        errors.a_levels = 'Please complete all A-Level entries or remove empty ones'
      }
    })
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-lg p-4 md:p-10">
    <div class="mb-4 md:mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">Qualifications</h2>
      <p class="text-sm text-gray-600">Tell us about your academic qualifications and credentials</p>
    </div>

    <div class="space-y-8">
      <!-- Degrees Section -->
      <div id="degrees-section" class="bg-white rounded-xl p-4 md:p-6 border-2 border-gray-200">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-md md:text-lg font-bold text-gray-900">
              Degree / Qualification Details <span class="text-red-500">*</span>
            </h3>
            <p class="hidden md:block text-sm text-gray-600 mt-1">Add your completed and current degrees</p>
          </div>
        </div>

        <!-- Degrees Error Message -->
        <div v-if="displayErrors.degrees" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
          <div class="flex items-start gap-3">
            <span class="material-symbols-outlined text-red-600 mt-0.5">error</span>
            <p class="text-red-600 font-medium text-sm">{{ displayErrors.degrees }}</p>
          </div>
        </div>

        <div v-if="modelValue.degrees && modelValue.degrees.length > 0" id="degree-entries" class="space-y-6">
          <div
            v-for="(degree, index) in modelValue.degrees"
            :key="index"
            class="degree-entry bg-gray-50 rounded-xl p-6 border-2 border-gray-200"
          >
            <!-- Row 1: Status & Type -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                  Degree Status <span class="text-red-500">*</span>
                </label>
                <select
                  :value="degree.status"
                  @change="updateDegree(index, 'status', $event.target.value)"
                  class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
                >
                  <option value="">Select status</option>
                  <option v-for="status in degreeStatuses" :key="status.value" :value="status.value">
                    {{ status.label }}
                  </option>
                </select>
                <p v-if="displayErrors[`degrees.${index}.status`]" class="text-red-500 text-sm mt-1">
                  {{ displayErrors[`degrees.${index}.status`] }}
                </p>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                  Degree Type <span class="text-red-500">*</span>
                </label>
                <select
                  :value="degree.degree_type"
                  @change="updateDegree(index, 'degree_type', $event.target.value)"
                  class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
                >
                  <option value="">Select type</option>
                  <option v-for="type in degreeTypes" :key="type" :value="type">
                    {{ type }}
                  </option>
                </select>
                <p v-if="displayErrors[`degrees.${index}.degree_type`]" class="text-red-500 text-sm mt-1">
                  {{ displayErrors[`degrees.${index}.degree_type`] }}
                </p>
              </div>
            </div>

            <!-- Row 2: Title -->
            <div class="mb-4">
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Qualification Title <span class="text-red-500">*</span>
              </label>
              <input
                type="text"
                :value="degree.title"
                @input="updateDegree(index, 'title', $event.target.value)"
                placeholder="e.g. Mathematics, Secondary Education"
                class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 bg-white"
              />
              <p v-if="displayErrors[`degrees.${index}.title`]" class="text-red-500 text-sm mt-1">
                {{ displayErrors[`degrees.${index}.title`] }}
              </p>
            </div>

            <!-- Row 3: Institution -->
            <div class="mb-4">
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                University / Institution <span class="text-red-500">*</span>
              </label>
              <input
                type="text"
                :value="degree.institution"
                @input="updateDegree(index, 'institution', $event.target.value)"
                placeholder="e.g. University of Cambridge"
                class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 bg-white"
              />
              <p v-if="displayErrors[`degrees.${index}.institution`]" class="text-red-500 text-sm mt-1">
                {{ displayErrors[`degrees.${index}.institution`] }}
              </p>
            </div>

            <!-- Row 4: Start & End Year -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="hidden md:inline">Start Year</span>
                    <span class="md:hidden">Year</span>
                    <span class="text-red-500">*</span>
                </label>
                <select
                  :value="degree.start_year"
                  @change="updateDegree(index, 'start_year', $event.target.value)"
                  class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
                >
                  <option value="">{{ selectYearLabel }}</option>
                  <option v-for="year in startYears" :key="year" :value="year">{{ year }}</option>
                  <option value="earlier">Earlier</option>
                </select>
                <p v-if="displayErrors[`degrees.${index}.start_year`]" class="text-red-500 text-sm mt-1">
                  {{ displayErrors[`degrees.${index}.start_year`] }}
                </p>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                  <span class="hidden md:inline">End Year / Expected Graduation</span>
                  <span class="md:hidden">End Year</span>
                </label>
                <select
                  :value="degree.end_year"
                  @change="updateDegree(index, 'end_year', $event.target.value)"
                  class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
                >
                    <option value="">{{ selectYearLabel }}</option>
                  <option v-for="year in endYears" :key="year" :value="year">{{ year }}</option>
                  <option value="earlier">Earlier</option>
                </select>
              </div>
            </div>

            <!-- Row 5: Classification & Remove Button -->
            <div :class="canRemoveDegree ? 'grid md:grid-cols-2 gap-4' : ''">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Degree Classification</label>
                <select
                  :value="degree.classification"
                  @change="updateDegree(index, 'classification', $event.target.value)"
                  class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
                >
                  <option value="">Select classification</option>
                  <option v-for="classification in degreeClassifications" :key="classification" :value="classification">
                    {{ classification }}
                  </option>
                </select>
              </div>
              <div v-if="canRemoveDegree" class="flex items-end">
                <button
                  type="button"
                  @click="removeDegree(index)"
                  class="w-full px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all font-medium flex items-center justify-center"
                >
                  <span class="material-symbols-outlined mr-2">delete</span>
                  Remove Degree
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          @click="addDegree"
          class="w-full mt-4 bg-blue-50 border-2 border-dashed border-brand-blue rounded-lg p-4 text-center hover:bg-blue-100 transition-all flex items-center justify-center"
        >
          <span class="material-symbols-outlined text-brand-blue mr-2">add_circle</span>
          <span class="text-brand-blue font-semibold">Add {{ modelValue.degrees?.length ? 'Another ' : '' }}Degree</span>
        </button>
      </div>

      <!-- A-Level Qualifications Section -->
      <div id="alevels-section" class="bg-white rounded-xl p-4 md:p-6 border-2 border-gray-200">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-base md:text-lg font-bold text-gray-900">
              A-Level Qualifications <span class="text-red-500">*</span>
            </h3>
            <p class="hidden md:block text-sm text-gray-600 mt-1">Add your A-Level subjects and grades</p>
          </div>
        </div>

        <!-- A-Level Error Message -->
        <div v-if="displayErrors.a_levels" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
          <div class="flex items-start gap-3">
            <span class="material-symbols-outlined text-red-600 mt-0.5">error</span>
            <p class="text-red-600 font-medium text-sm">{{ displayErrors.a_levels }}</p>
          </div>
        </div>

        <div v-if="modelValue.a_levels && modelValue.a_levels.length > 0" id="alevel-entries" class="space-y-4">
          <div
            v-for="(aLevel, index) in modelValue.a_levels"
            :key="index"
            class="alevel-entry grid grid-cols-12 gap-4 items-end"
          >
            <div class="col-span-6">
              <label v-if="index === 0" class="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
              <select
                :value="aLevel.subject"
                @change="updateALevel(index, 'subject', $event.target.value)"
                class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
              >
                <option value="">Select subject</option>
                <option v-for="subject in aLevelSubjects" :key="subject" :value="subject">
                  {{ subject }}
                </option>
              </select>
              <p v-if="displayErrors[`a_levels.${index}.subject`]" class="text-red-500 text-sm mt-1">
                {{ displayErrors[`a_levels.${index}.subject`] }}
              </p>
            </div>
            <div class="col-span-4">
              <label v-if="index === 0" class="block text-sm font-semibold text-gray-700 mb-2">Grade</label>
              <select
                :value="aLevel.grade"
                @change="updateALevel(index, 'grade', $event.target.value)"
                class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
              >
                <option value="">Select grade</option>
                <option v-for="grade in aLevelGrades" :key="grade" :value="grade">
                  {{ grade }}
                </option>
              </select>
              <p v-if="displayErrors[`a_levels.${index}.grade`]" class="text-red-500 text-sm mt-1">
                {{ displayErrors[`a_levels.${index}.grade`] }}
              </p>
            </div>
            <div class="col-span-2">
              <button
                v-if="canRemoveALevel"
                type="button"
                @click="removeALevel(index)"
                class="w-full px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all font-medium flex items-center justify-center"
              >
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          @click="addALevel"
          class="w-full mt-4 bg-blue-50 border-2 border-dashed border-brand-blue rounded-lg p-4 text-center hover:bg-blue-100 transition-all flex items-center justify-center"
        >
          <span class="material-symbols-outlined text-brand-blue mr-2">add_circle</span>
          <span class="text-brand-blue font-semibold">Add {{ modelValue.a_levels?.length ? 'Another ' : '' }}A-Level</span>
        </button>
      </div>

      <!-- Years of Teaching Experience -->
      <div id="years-experience-field">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          Years of Teaching Experience <span class="text-red-500">*</span>
        </label>
        <div class="grid grid-cols-3 md:grid-cols-5 gap-3">
          <button
            v-for="(experience, idx) in experienceRanges"
            :key="experience.value"
            type="button"
            @click="selectExperience(experience)"
            class="bg-white border-2 rounded-xl p-4 text-center hover:border-brand-blue hover:bg-blue-50 transition-all"
            :class="[modelValue.years_of_experience === experience.value ? 'border-brand-blue bg-blue-50' : 'border-gray-200', idx === (experienceRanges.length -1) ? 'col-span-2' : '']"
          >
            <p class="font-bold text-gray-900">{{ experience.label }}</p>
            <p class="text-xs text-gray-500 mt-1">{{ experience.description }}</p>
          </button>
        </div>
        <p v-if="displayErrors.years_of_experience" class="text-red-500 text-sm mt-2">{{ displayErrors.years_of_experience }}</p>
      </div>

      <!-- Additional Qualifications -->
      <div id="additional-qualifications-field">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          Additional Qualifications & Certifications
        </label>
        <textarea
          :value="modelValue.additional_qualifications"
          @input="updateField('additional_qualifications', $event.target.value)"
          placeholder="List any other relevant qualifications, certifications, or professional development courses (e.g., QTS, PGCE, DBS clearance, safeguarding training)"
          rows="4"
          class="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 resize-none bg-white"
        ></textarea>
        <p class="text-xs text-gray-500 mt-2">Include teaching certifications, subject-specific qualifications, or specialized training</p>
      </div>
    </div>
  </div>
</template>
